import React from 'react';
import BaseScadaNode from './BaseScadaNode';
import { GateIndicator } from './RibbonBlenderNode';
import { isGateOpen } from './signal_utils';

/**
 * CoolerBlender SVG
 * - Jacket (blue dashed border) = cooling water circuit
 * - Montante (metal standpipe/riser) at the top = vapor inlet from cyclone
 * - 1 product outlet at bottom center
 * - Ribbon inside shown in cooler color scheme
 */
export function CoolerBlenderSVG({ config = {}, width = 110, height = 70, liveValues = {} }) {
    const threshold = parseFloat(config.threshold ?? 0.5);
    const stateVal = liveValues.state;
    const running = stateVal != null && parseFloat(stateVal) >= threshold;
    const color = running ? (config.run_color || '#22C55E') : (config.stop_color || '#64748B');

    const cx = width / 2;
    const bodyW = width - 20;
    const bodyH = height * 0.52;
    const bodyX = 10;
    // Push body down to leave room for montante at top
    const montanteH = height * 0.18;
    const montanteW = 10;
    const bodyY = montanteH + 4;

    const outletY = bodyY + bodyH;

    const outletSensorType = config.outlet_sensor_type || 'single';
    const outletOpenLevel  = config.outlet_open_level  || 'high';
    const outletOpen = isGateOpen(liveValues, 'outlet_state', 'outlet_state_close',
        outletSensorType, outletOpenLevel, threshold);
    const gateW = 14;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Jacket — cooling water (blue dashed) */}
            <rect
                x={bodyX - 5} y={bodyY - 4}
                width={bodyW + 10} height={bodyH + 8}
                rx={8}
                fill="rgba(59,130,246,0.12)"
                stroke="#3B82F6"
                strokeWidth={1.5}
                strokeDasharray="4 2"
            />

            {/* Montante (metal riser) at top center — vapor inlet */}
            <rect
                x={cx - montanteW / 2} y={0}
                width={montanteW} height={montanteH + 4}
                rx={2}
                fill="#334155"
                stroke="#64748B"
                strokeWidth={1.5}
            />
            {/* Flange at top of montante */}
            <rect
                x={cx - montanteW / 2 - 4} y={0}
                width={montanteW + 8} height={4}
                rx={1}
                fill="#475569"
                stroke="#64748B"
                strokeWidth={1}
            />
            {/* Dashed line inside montante indicating vapor flow */}
            <line
                x1={cx} y1={4} x2={cx} y2={bodyY}
                stroke="#60A5FA"
                strokeWidth={1}
                strokeDasharray="3 2"
                opacity={0.6}
            />

            {/* Main body */}
            <rect
                x={bodyX} y={bodyY}
                width={bodyW} height={bodyH}
                rx={bodyH / 2}
                fill="#1E293B"
                stroke={color}
                strokeWidth={2}
            />

            {/* Ribbon blades (simplified — 2 arcs) */}
            <path
                d={`M ${bodyX + 8},${bodyY + 4} Q ${cx - 10},${bodyY + bodyH - 4} ${cx + 10},${bodyY + 4} Q ${bodyX + bodyW - 8},${bodyY + bodyH - 4} ${bodyX + bodyW - 8},${bodyY + bodyH / 2}`}
                stroke={color}
                strokeWidth={2}
                fill="none"
                opacity={0.8}
            />
            <path
                d={`M ${bodyX + 8},${bodyY + bodyH - 4} Q ${cx - 10},${bodyY + 4} ${cx + 10},${bodyY + bodyH - 4} Q ${bodyX + bodyW - 8},${bodyY + 4} ${bodyX + bodyW - 8},${bodyY + bodyH / 2}`}
                stroke="#60A5FA"
                strokeWidth={1.5}
                fill="none"
                opacity={0.7}
            />

            {/* Drive shaft / motor stub (left side) */}
            <rect
                x={bodyX - 8} y={(bodyY + bodyH / 2) - 6}
                width={8} height={12}
                rx={3}
                fill="#334155"
                stroke={color}
                strokeWidth={1.5}
            />
            {/* Top shaft cap */}
            <ellipse cx={cx} cy={bodyY} rx={14} ry={4} fill="#334155" stroke={color} strokeWidth={1.5} />

            {/* Outlet leg (bottom center) */}
            <line
                x1={cx} y1={outletY}
                x2={cx} y2={outletOpen !== null ? height - 14 : height - 2}
                stroke="#475569"
                strokeWidth={4}
            />
            <GateIndicator
                x={cx - gateW / 2}
                y={height - 12}
                width={gateW}
                open={outletOpen}
                color_open={config.on_color}
                color_closed={config.off_color}
            />
        </svg>
    );
}

export default function CoolerBlenderNode(props) {
    const { data } = props;
    const w = data?.width || 110;
    const h = data?.height || 70;
    const tempVal = data?.liveValues?.temp;
    const tempBound = tempVal != null;

    return (
        <BaseScadaNode {...props}>
            <CoolerBlenderSVG
                config={data?.config}
                width={w}
                height={h}
                liveValues={data?.liveValues || {}}
            />
            {/* Temperature badge — only shown when signal is bound */}
            {tempBound && (
                <div style={{
                    position: 'absolute',
                    bottom: data?.label ? 22 : 4,
                    right: 4,
                    background: '#1E293B',
                    border: '1px solid #3B82F6',
                    borderRadius: 4,
                    padding: '1px 5px',
                    fontSize: 9,
                    color: '#3B82F6',
                    fontFamily: 'monospace',
                    pointerEvents: 'none',
                }}>
                    {isNaN(parseFloat(tempVal)) ? '--' : parseFloat(tempVal).toFixed(1)}{data?.config?.unit || '°C'}
                </div>
            )}
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
